import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';
import { Category } from '../types/category';
import CategoryDialog from '../components/CategoryDialog';
import CategoryIcon from '../components/CategoryIcon';
import PageHeader from '../components/PageHeader';
import ResponsiveDialog from '../components/ResponsiveDialog';
import { useNavigate } from 'react-router-dom';

const CategoriesPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');

  // Vérifier l'authentification
  React.useEffect(() => {
    if (!apiService.isAuthenticated()) {
      navigate('/login');
    }
  }, [navigate]);

  // Charger la préférence de vue depuis le localStorage
  React.useEffect(() => {
    const savedViewMode = localStorage.getItem('categoriesViewMode') as 'list' | 'grid';
    if (savedViewMode) {
      setViewMode(savedViewMode);
    }
  }, []);

  // Sauvegarder la préférence de vue dans le localStorage
  const handleViewModeChange = (event: React.MouseEvent<HTMLElement>, newViewMode: 'list' | 'grid') => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
      localStorage.setItem('categoriesViewMode', newViewMode);
    }
  };

  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiService.getCategories(),
    select: (response) => response.data.categories || [],
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => apiService.createCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setDialogOpen(false);
    },
    onError: (error: any) => {
      console.error('Erreur lors de la création de la catégorie:', error);
      // Ne pas rediriger automatiquement, laisser l'utilisateur voir l'erreur
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => apiService.updateCategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setDialogOpen(false);
    },
    onError: (error: any) => {
      console.error('Erreur lors de la mise à jour de la catégorie:', error);
      // Ne pas rediriger automatiquement, laisser l'utilisateur voir l'erreur
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setDeleteDialogOpen(false);
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      apiService.updateCategory(id, { is_active: isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
  });

  const handleCreateCategory = () => {
    setSelectedCategory(null);
    setDialogOpen(true);
  };

  const handleEditCategory = async (category: Category) => {
    try {
      const response = await apiService.getCategory(category.id);
      setSelectedCategory(response.data.category);
    } catch {
      setSelectedCategory(category);
    }
    setDialogOpen(true);
  };

  const handleDeleteCategory = (category: Category) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  const handleSaveCategory = (categoryData: any) => {
    if (selectedCategory) {
      updateMutation.mutate({ id: selectedCategory.id, data: categoryData });
    } else {
      createMutation.mutate(categoryData);
    }
  };

  const handleConfirmDelete = () => {
    if (categoryToDelete) {
      deleteMutation.mutate(categoryToDelete.id);
    }
  };

  const handleToggleActive = (category: Category) => {
    toggleActiveMutation.mutate({ id: category.id, isActive: !category.is_active });
  };

  if (error) {
    const err = error as { response?: { data?: { message?: string; error?: string }; status?: number } };
    const detail =
      err.response?.data?.message ||
      err.response?.data?.error ||
      (err.response?.status ? `HTTP ${err.response.status}` : '');
    return (
      <Alert severity="error">
        Erreur lors du chargement des catégories
        {detail ? ` : ${detail}` : ''}
        <Typography variant="body2" sx={{ mt: 1 }}>
          Si le problème persiste après reconnexion : exécutez{' '}
          <code>php bin/console doctrine:migrations:migrate</code> sur le serveur API (colonnes catégorie).
        </Typography>
      </Alert>
    );
  }

  return (
    <Box>
      <PageHeader
        title={
          <Typography variant="h4" component="h1">
            Gestion des Catégories
          </Typography>
        }
        actions={
          <>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
              aria-label="Mode d'affichage"
              sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, justifyContent: 'center' }}
            >
              <ToggleButton value="list" aria-label="Vue en ligne">
                <ViewListIcon />
              </ToggleButton>
              <ToggleButton value="grid" aria-label="Vue en grille">
                <ViewModuleIcon />
              </ToggleButton>
            </ToggleButtonGroup>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateCategory}>
              Nouvelle Catégorie
            </Button>
          </>
        }
      />

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : viewMode === 'list' ? (
        // Vue en ligne (liste)
        <Paper sx={{ width: '100%' }}>
          <List>
            {data?.map((category: Category) => (
              <ListItem
                key={category.id}
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon>
                  <CategoryIcon
                    icon={category.icon}
                    customIconFilename={category.custom_icon_filename}
                    size="small"
                    style={{ color: 'primary.main' }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="h6">
                        {category.name}
                      </Typography>
                      <Chip
                        label={category.is_active ? 'Active' : 'Inactive'}
                        color={category.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {category.description || 'Aucune description'}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Chip label={category.slug} size="small" variant="outlined" />
                        {(category.icon || category.custom_icon_filename) && (
                          <Box
                            component="span"
                            title={category.custom_icon_filename || category.icon}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              verticalAlign: 'middle',
                              p: 0.5,
                              borderRadius: 1,
                              border: 1,
                              borderColor: 'divider',
                              bgcolor: 'action.hover',
                            }}
                          >
                            <CategoryIcon
                              icon={category.icon}
                              customIconFilename={category.custom_icon_filename}
                              size="small"
                            />
                          </Box>
                        )}
                        <Chip 
                          label={`${Object.keys(category.attributes || {}).length} attribut(s)`} 
                          size="small" 
                          color="primary" 
                          variant="outlined" 
                        />
                        <Typography variant="caption" color="text.secondary">
                          Ordre: {category.sort_order}
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Box display="flex" alignItems="center" gap={1}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={category.is_active}
                          onChange={() => handleToggleActive(category)}
                          size="small"
                        />
                      }
                      label=""
                    />
                    <IconButton
                      onClick={() => handleEditCategory(category)}
                      color="primary"
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDeleteCategory(category)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Paper>
      ) : (
        // Vue en grille
        <Grid container spacing={3}>
          {data?.map((category: Category) => (
            <Grid item xs={12} sm={6} md={4} key={category.id}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box display="flex" alignItems="center">
                      <CategoryIcon
                        icon={category.icon}
                        customIconFilename={category.custom_icon_filename}
                        size="small"
                        style={{ marginRight: 8, color: 'primary.main' }}
                      />
                      <Typography variant="h6" component="h2">
                        {category.name}
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={category.is_active}
                          onChange={() => handleToggleActive(category)}
                          size="small"
                        />
                      }
                      label=""
                    />
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {category.description || 'Aucune description'}
                  </Typography>

                  <Box display="flex" alignItems="center" mb={2}>
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                      Slug:
                    </Typography>
                    <Chip label={category.slug} size="small" variant="outlined" />
                  </Box>

                  {(category.icon || category.custom_icon_filename) && (
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <Typography variant="caption" color="text.secondary">
                        Icône :
                      </Typography>
                      <Box
                        title={category.custom_icon_filename || category.icon}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          p: 0.75,
                          borderRadius: 1,
                          border: 1,
                          borderColor: 'divider',
                          bgcolor: 'action.hover',
                        }}
                      >
                        <CategoryIcon
                          icon={category.icon}
                          customIconFilename={category.custom_icon_filename}
                          size="medium"
                        />
                      </Box>
                    </Box>
                  )}

                  <Box display="flex" alignItems="center" mb={2}>
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                      Attributs:
                    </Typography>
                    <Chip 
                      label={`${Object.keys(category.attributes || {}).length} attribut(s)`} 
                      size="small" 
                      color="primary" 
                      variant="outlined" 
                    />
                  </Box>

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      Ordre: {category.sort_order}
                    </Typography>
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => handleEditCategory(category)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteCategory(category)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialog pour créer/modifier une catégorie */}
      <CategoryDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        category={selectedCategory}
        onSave={handleSaveCategory}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />

      {/* Dialog de confirmation de suppression */}
      <ResponsiveDialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm">
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer la catégorie "{categoryToDelete?.name}" ?
            Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Annuler</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? <CircularProgress size={20} /> : 'Supprimer'}
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </Box>
  );
};

export default CategoriesPage;
